import { GroupCodes } from '../../@types/Groups';
import Joi from 'joi';

const BH_PRODUCT_ALLOWED_TYPES: string[] = ['BHINIT', 'BHMD', 'BHNPT'];
const REG_TYPE_CLIENT_SITE: string = 'client site';
const REG_TYPE_RTE: string = 'rte';
const REG_TYPE_STAGED: string = 'staged';

const isUnique = (array) => new Set(array).size !== array.length;
const parseCodes = (codes) =>
  codes
    .map(({ code }) => code.split(';'))
    .flat()
    .filter((code) => code);

export const codeReorganizer = (codes: GroupCodes[]) =>
  codes
    .reduce((codes: GroupCodes[][], current: GroupCodes) => {
      codes.push(
        current.code
          .split(';')
          .filter((code) => code)
          .map((item) => ({ ...current, code: item }))
      );
      return codes;
    }, [])
    .flat();

const checkUnique = (value, { message, state, error }) => {
  if (value.match(/[^A-Za-z0-9;]+/g)) {
    return error('any.invalid');
  }

  const { newCodes, editCodes } = state.ancestors[2];
  const codes = [...parseCodes(newCodes), ...parseCodes(editCodes)];
  if (isUnique(codes)) {
    return message('Codes must be unique');
  }

  return value;
};

const productTypeSchemaObject: Joi.StringSchema = Joi.string()
  .when('code', {
    is: 'BH',
    then: Joi.valid(...BH_PRODUCT_ALLOWED_TYPES).required(),
    otherwise: Joi.string().optional().allow(null, '')
  })
  .when('code', {
    is: 'EMS',
    then: Joi.valid('EFB').optional(),
    otherwise: Joi.string().optional().allow(null, '')
  });

const product = {
  isEnabled: Joi.string(),
  code: Joi.string(),
  start_dt: Joi.when('isEnabled', {
    is: 'true',
    then: Joi.date().iso().required(),
    otherwise: Joi.optional().allow(null)
  }).label('Effective date'),
  end_dt: Joi.date().iso().allow(null, ''),
  member_copay: Joi.number().optional().allow(''),
  cost_per_person: Joi.number().optional().allow(''),
  extended_family: Joi.boolean().optional(),
  could_be_less: Joi.boolean().optional(),
  app_opt_out: Joi.boolean().optional(),
  type: productTypeSchemaObject
};

const products = {
  could_be_less: Joi.string().allow('Y', 'N').optional(),
  app_opt_out: Joi.string().allow('Y', 'N').optional(),
  GM: Joi.object({
    ...product
  }).optional(),
  DERM: Joi.object({
    ...product
  }).optional(),
  BH: Joi.object({
    ...product,
    BHINIT: Joi.number().optional().allow(''),
    BHMD: Joi.number().optional().allow(''),
    BHNPT: Joi.number().optional().allow(''),
    enable_adolescent_visits: Joi.boolean()
  }).optional(),
  EMS: Joi.object({
    ...product,
    isEfb: Joi.boolean().optional()
  }).optional()
};

const futureProducts = {
  could_be_less: Joi.string().allow('Y', 'N'),
  app_opt_out: Joi.string().allow('Y', 'N').optional(), // Made optional since it's now GM-specific
  GM: Joi.object({
    code: Joi.string(),
    start_dt: Joi.date().iso().allow(null, ''),
    end_dt: Joi.date().iso().allow(null, ''),
    member_copay: Joi.number().optional().allow(''),
    could_be_less: Joi.boolean().optional(),
    app_opt_out: Joi.boolean().optional(),
    isEnabled: Joi.boolean()
  }),
  DERM: Joi.object({
    code: Joi.string(),
    start_dt: Joi.date().iso().allow(null, ''),
    end_dt: Joi.date().iso().allow(null, ''),
    member_copay: Joi.number().optional().allow(''),
    could_be_less: Joi.boolean().optional(),
    isEnabled: Joi.boolean()
  }),
  BH: Joi.object({
    code: Joi.string(),
    BHINIT: Joi.number().optional().allow(''),
    BHMD: Joi.number().optional().allow(''),
    BHNPT: Joi.number().optional().allow(''),
    start_dt: Joi.date().iso().allow(null, ''),
    end_dt: Joi.date().iso().allow(null, ''),
    member_copay: Joi.number().optional().allow(''),
    could_be_less: Joi.boolean().optional(),
    isEnabled: Joi.boolean()
  })
};

const baseCodeSchema: Joi.ObjectSchema = Joi.object({
  code: Joi.string().required(),
  start_dt: Joi.date().iso().required(),
  end_dt: Joi.date().iso().optional().allow(null, '')
});

const requiredCodesSchema: Joi.ArraySchema = Joi.array().items(baseCodeSchema).min(1).required();
const optionalCodesSchema: Joi.ArraySchema = Joi.array().items(baseCodeSchema).optional().allow(null);

const requiredCodesObject: object = {
  code: Joi.string().required(),
  classCodes: requiredCodesSchema,
  bpCodes: requiredCodesSchema
};

const optionalClassBpCodesPlanCodesSchema: Joi.ArraySchema = Joi.array()
  .items(
    Joi.object({
      code: Joi.string().optional().allow(null, ''),
      classCodes: optionalCodesSchema,
      bpCodes: optionalCodesSchema
    })
  )
  .allow(Joi.array().empty());

const buildRequiredCodesSchemaWithOptionalEntry: (entry: string) => Joi.ArraySchema = (
  entry: string
): Joi.ArraySchema => {
  return Joi.array().items(
    Joi.object({
      ...requiredCodesObject,
      [entry]: optionalCodesSchema
    })
  );
};
const requiredClassBpCodesPlanCodesSchema: Joi.ArraySchema = Joi.array().items(Joi.object(requiredCodesObject));
const optionalClassCodesSchema: Joi.ArraySchema = buildRequiredCodesSchemaWithOptionalEntry('classCodes');
const optionalBpCodesSchema: Joi.ArraySchema = buildRequiredCodesSchemaWithOptionalEntry('bpCodes');

const group = {
  id: Joi.string().hex().length(24).optional().allow(null, ''),
  legacyGroupId: Joi.string().optional().allow(null, ''),
  start_dt: Joi.date().iso().required().label('Effective date'),
  end_dt: Joi.date().iso().optional().allow(null, ''),
  name: Joi.string().required().label('Plan Name'),
  language: Joi.string().label('Language'),
  reg_type: Joi.string().required().label('Eligibility Type'),
  selectedPlanNumber: Joi.string().optional().allow(''),
  planCodes: Joi.when('reg_type', { is: REG_TYPE_CLIENT_SITE, then: optionalClassBpCodesPlanCodesSchema })
    .concat(Joi.when('reg_type', { is: REG_TYPE_STAGED, then: optionalBpCodesSchema }))
    .concat(Joi.when('reg_type', { is: REG_TYPE_RTE, then: optionalClassCodesSchema }))
    .concat(
      Joi.when('end_dt', {
        is: Joi.date().iso().max('now'),
        then: optionalClassBpCodesPlanCodesSchema,
        otherwise: requiredClassBpCodesPlanCodesSchema
      })
    ),
  products: Joi.object(products),
  futureProducts: Joi.object(futureProducts),
  status: Joi.string().optional().allow(null, '')
};

const contact = {
  first: Joi.string().required().label('First Name'),
  last: Joi.string().required().label('Last Name'),
  email: Joi.string()
    .pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    .message('Invalid email')
    .required()
    .label('Email'),
  type: Joi.string()
};
const contacts = {
  Account: Joi.object({
    ...contact,
    phone: Joi.string().length(10).required().label('Contact phone'),
    __auditTempId: Joi.string().optional().allow(null, '')
  }).required(),
  SAE: Joi.object({
    ...contact,
    phone: Joi.string().length(10).allow(null, '').label('Contact phone'),
    __auditTempId: Joi.string().optional().allow(null, '')
  }).required(),
  Broker: Joi.object({
    first: Joi.string().optional().allow(null, '').label('First Name'),
    last: Joi.string().optional().allow(null, '').label('Last Name'),
    email: Joi.string()
      .pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
      .message('Invalid email')
      .optional()
      .allow(null, '')
      .label('Email'),
    type: Joi.string().optional().allow(null, ''),
    company: Joi.string().optional().allow(null, ''),
    phone: Joi.string().length(10).optional().allow(null, '').label('Contact phone'),
    __auditTempId: Joi.string().optional().allow(null, '')
  }).optional(),
  Marketing: Joi.array()
    .items(
      Joi.object({
        ...contact,
        __auditTempId: Joi.string().optional().allow(null, '')
      }).optional()
    )
    .optional()
};

export const publishSchema = Joi.object({
  _id: Joi.alternatives(Joi.number(), Joi.string().hex().length(24)),
  Identifier: Joi.string().optional().allow(null, ''),
  CreatedAt: Joi.string().optional().allow(null, ''),
  CreatedBy: Joi.string().optional().allow(null, ''),
  CreatedByName: Joi.string().optional().allow(null, ''),
  UpdatedAt: Joi.string().optional().allow(null, ''),
  UpdatedBy: Joi.string().optional().allow(null, ''),
  UpdatedByName: Joi.string().optional().allow(null, ''),
  planNumber: Joi.string().min(3).pattern(/^\S+$/).message('Spaces are not allowed').required(),
  name: Joi.string().trim().min(1).required(),
  start_dt: Joi.date().iso().required().label('Effective date'),
  end_dt: Joi.date().iso().optional().allow(null, ''),
  total_employee: Joi.number(),
  total_lives: Joi.number(),
  instructions: Joi.string().optional().allow(null, ''),
  street1: Joi.string().optional().allow(null, ''),
  street2: Joi.string().optional().allow(null, ''),
  city: Joi.string().optional().allow(null, ''),
  state: Joi.string().optional().allow(null, ''),
  zip: Joi.string().length(5).optional().allow(null, ''),
  templateName: Joi.string().optional().allow(null, ''),
  logo: Joi.optional().allow(null, ''),
  contacts: Joi.object(contacts),
  groups: Joi.array().items(group).min(1)
});

export const codesValidation = Joi.object({
  contentType: Joi.string(),
  newCodes: Joi.array().items({
    code: Joi.string().required().custom(checkUnique).label('Code'),
    start_dt: Joi.date().iso().optional().allow(null, '').label('Effective date'),
    end_dt: Joi.date().iso().optional().allow(null, '').label('Termination date')
  }),
  editCodes: Joi.array().items({
    code: Joi.string().required().custom(checkUnique).label('Code'),
    start_dt: Joi.date().iso().required().label('Effective date'),
    end_dt: Joi.date().iso().optional().allow(null, '').label('Termination date'),
    readOnly: Joi.boolean().optional()
  }),
  checkedCodes: Joi.array().items(Joi.string()),
  multiEditAction: Joi.string(),
  multiEditValue: Joi.date().iso()
});

export const terminateValidation = Joi.object({
  end_dt: Joi.date().iso().required().label('Termination date'),
  terminateReason: Joi.string().required().label('Reason for Termination'),
  customReason: Joi.when('terminateReason', {
    is: 'Other',
    then: Joi.string().required().min(3),
    otherwise: Joi.optional().allow('')
  }).label('Notes')
});

export const newGroupValidation = Joi.object({
  name: Joi.string().trim().min(1).required().label('Group Name'),
  planNumber: Joi.string().min(3).pattern(/^\S+$/).message('Spaces are not allowed').required().label('Group Number')
});
